import React from 'react';
import { ArrowUpRight, Github, Globe, Package } from 'lucide-react';
import { featuredProjects, minorProjects } from '../data/profile';
import { FeaturedProject, Link } from '../types';
import ArchDiagram from './ArchDiagram';
import { Chip, Rich, Section } from './ui';

const linkIcon = (label: Link['label']) =>
  label === 'GitHub' ? <Github size={14} /> : label === 'npm' ? <Package size={14} /> : <Globe size={14} />;

const ProjectDetails: React.FC<{ project: FeaturedProject }> = ({ project }) => (
  <>
    <div className="flex items-start justify-between gap-4">
      <span className="font-mono text-[11px] uppercase tracking-wider text-fg-faint">{project.kind}</span>
      <div className="flex shrink-0 gap-2">
        {project.links.map((link) => (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-ink-600 px-2.5 py-1 text-xs text-fg-muted transition-colors hover:border-accent/60 hover:text-accent"
          >
            {linkIcon(link.label)}
            {link.label}
          </a>
        ))}
      </div>
    </div>

    <h3 className="mt-5 text-2xl font-medium tracking-tight text-fg">{project.name}</h3>
    <p className="mt-2 text-fg-muted">{project.tagline}</p>

    <dl className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-ink-700 bg-ink-700 sm:grid-cols-3">
      {project.specs.map((spec) => (
        <div key={spec.label} className="bg-ink-900 px-3 py-2.5">
          <dt className="font-mono text-[10px] uppercase tracking-wider text-fg-faint">{spec.label}</dt>
          <dd className="mt-0.5 text-sm text-fg">{spec.value}</dd>
        </div>
      ))}
    </dl>
    {project.specsNote && <p className="mt-2 font-mono text-[10px] text-fg-faint">* {project.specsNote}</p>}

    <ul className="mt-6 space-y-3">
      {project.points.map((point) => (
        <li key={point} className="flex gap-3 text-[15px] leading-relaxed text-fg-muted">
          <span className="mt-[11px] h-px w-3 shrink-0 bg-accent/70" aria-hidden />
          <span>
            <Rich text={point} />
          </span>
        </li>
      ))}
    </ul>

    <div className="mt-auto flex flex-wrap gap-1.5 pt-6">
      {project.stack.map((tech) => (
        <Chip key={tech}>{tech}</Chip>
      ))}
    </div>
  </>
);

// Diagrams alternate sides so the section reads as a rhythm rather than a stack.
const ProjectCard: React.FC<{ project: FeaturedProject; flipped: boolean }> = ({ project, flipped }) => (
  <article
    className={`reveal spotlight grid gap-10 rounded-2xl border border-ink-700 bg-ink-900/60 p-6 transition-colors hover:border-ink-600 sm:p-8 lg:items-center ${
      flipped ? 'lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]' : 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]'
    }`}
  >
    <div className="flex h-full min-w-0 flex-col">
      <ProjectDetails project={project} />
    </div>
    <div className={`hidden min-w-0 sm:block ${flipped ? 'lg:order-first' : ''}`}>
      <ArchDiagram diagram={project.diagram} />
    </div>
  </article>
);

const Projects: React.FC = () => (
  <Section
    id="projects"
    index="02"
    eyebrow="Projects"
    title={
      <>
        Things I’ve <em className="text-accent">built</em>.
      </>
    }
  >
    <div className="space-y-4">
      {featuredProjects.map((project, i) => (
        <ProjectCard key={project.name} project={project} flipped={i % 2 === 1} />
      ))}
    </div>

    <div className="mt-16">
      <h3 className="reveal mb-2 flex items-baseline justify-between font-mono text-xs uppercase tracking-[0.2em] text-fg-faint">
        More work
        <a href="https://github.com/ABHINAVGARG05?tab=repositories" target="_blank" rel="noopener noreferrer" className="normal-case tracking-normal text-fg-muted transition-colors hover:text-accent">
          All repositories →
        </a>
      </h3>
      <ul className="divide-y divide-ink-700 border-y border-ink-700">
        {minorProjects.map((project) => {
          const body = (
            <>
              <span className="font-mono text-xs text-fg-faint">{project.year}</span>
              <span>
                <span className="flex items-center gap-1.5 text-base font-medium text-fg transition-colors group-hover:text-accent">
                  {project.name}
                  {project.href && (
                    <ArrowUpRight size={15} className="opacity-50 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:opacity-100" />
                  )}
                </span>
                <span className="mt-1 block text-sm leading-relaxed text-fg-muted">{project.description}</span>
              </span>
              <span className="font-mono text-[11px] text-fg-faint sm:text-right">{project.stack.join(' · ')}</span>
            </>
          );
          const rowClass = 'grid gap-2 py-5 sm:grid-cols-[72px_1fr_200px] sm:items-baseline sm:gap-6';

          return (
            <li key={project.name} className="reveal">
              {project.href ? (
                <a href={project.href} target="_blank" rel="noopener noreferrer" className={`group ${rowClass}`}>
                  {body}
                </a>
              ) : (
                <div className={rowClass}>{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  </Section>
);

export default Projects;
